import type { autorun, reaction } from "mobx";

type TDisposer = () => void;

type ReflectMetadata = (
  metadataKey: unknown,
  metadataValue: unknown,
) => (target: object, propertyKey?: string | symbol) => void;

type ReflectDecorate = (
  decorators: Array<
    ClassDecorator | PropertyDecorator | MethodDecorator | ParameterDecorator
  >,
  target: object,
  propertyKey?: string | symbol,
  attributes?: PropertyDescriptor | null,
) => unknown;

type TReflect = typeof Reflect & {
  metadata?: ReflectMetadata;
  decorate?: ReflectDecorate;
};

export const OBJECT = Object;
export const ASSIGN = OBJECT.assign;
export const PARENT = "parent" as const;
export const VIEW_PROPS = "viewProps" as const;
export const REFLECT: TReflect = Reflect as TReflect;

/** A base class for view models */
export abstract class ViewModel<
  V extends ViewModel | null | unknown = unknown,
  P = unknown,
> {
  /** An array of disposers which are called after the view becomes unmounted */
  private d: TDisposer[] = [];

  /** Properties that were given to a view. This object is updated every time the view is rendered with the new props */
  readonly viewProps: Readonly<P>;

  /**
   * A view model instance which is defined in a view that contains the view of current view model.
   *
   * @example
   * // View1 creates an instance of ViewModel1
   * // View2 creates and instance of ViewModel2
   * // In this case ViewModel1 is a parent of ViewModel2, because View2 is located somewhere inside View1
   * <View1>
   *   <div>
   *     Other content
   *     <View2 />
   *   </div>
   * </View1>
   */
  readonly parent: V;

  /**
   * Add-on function for MobX's reaction. The disposer of the reaction will be automatically called after the view
   * becomes unmounted.
   */
  protected reaction: typeof reaction;

  /**
   * Add-on function for MobX's autorun. The disposer of the autorun will be automatically called after the view
   * becomes unmounted.
   */
  protected autorun: typeof autorun;

  constructor() {
    this.parent = (PROTOTYPE[PARENT] ?? null) as V;
    this.viewProps = (PROTOTYPE[VIEW_PROPS] ?? {}) as Readonly<P>;
    this.reaction = PROTOTYPE.reaction;
    this.autorun = PROTOTYPE.autorun;
  }

  /** A function for adding a disposer, that will be automatically called after the view becomes unmounted */
  protected addDisposer(disposer: TDisposer): void {
    this.d.push(disposer);
  }

  /** A function that is called after the View has become mounted. Calls in the {@link React.useEffect} hook */
  protected onViewMounted?(): void;

  /** A function that is called after the View has been rendered. Calls in the {@link React.useEffect} hook */
  protected onViewUpdated?(): void;

  /** A function that is called after the View has become unmounted. Calls in the {@link React.useEffect} hook */
  protected onViewUnmounted?(): void;

  /** A function that is called after the View has become mounted. Calls in the {@link React.useLayoutEffect} hook */
  protected onViewMountedSync?(): void;

  /** A function that is called after the View has been rendered. Calls in the {@link React.useLayoutEffect} hook */
  protected onViewUpdatedSync?(): void;

  /** A function that is called after the View has become unmounted. Calls in the {@link React.useLayoutEffect} hook */
  protected onViewUnmountedSync?(): void;
}

// You may think that all the code below is a crutch. But it's actually not. All these lines are needed for
// optimisations.
//
// For example, you may ask why do reaction and autorun declared in this way. Well, these functions are add-on
// functions, which means they should have the same type as their alternatives from MobX. And it's actually a problem.
// Reaction has generics and the number of generics is not constant in different versions of MobX. So, the only way to
// type it is using typeof keyword. In this case I could declare these functions as class members, but in this way all
// view models start creating these functions during initialization. Which is not much, but will affect memory
// consumption. Therefore, I decided to declare the typing of these functions as a member of the class, but really
// declare it in the prototype of the class. In this case, only one function will be created for all view models.
//
// And why don't I use decorators instead of using Reflect? Because in this case there'll be a lot of code generated
// in the main file. Using decorators instead of Reflect brings extra 500 characters which is 33% of the whole package.

type ViewModelPrototype = ViewModel<unknown, unknown> & {
  [PARENT]?: ViewModel<unknown, unknown> | null;
  [VIEW_PROPS]?: Readonly<unknown>;
  reaction: typeof reaction;
  autorun: typeof autorun;
};

export const PROTOTYPE = ViewModel.prototype as ViewModelPrototype;
